import { useMutation, useQueries, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  Building2,
  CheckSquare,
  ChevronDown,
  FolderKanban,
  MoreVertical,
  Plus,
  Search,
  Target,
  TrendingUp,
} from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Empty, EmptyDescription, EmptyTitle } from "@/components/ui/empty";
import { Skeleton } from "@/components/ui/skeleton";

import { useApi } from "@/api";
import {
  duplicateProject,
  listObjectives,
  listProjects,
  patchObjective,
  patchProject,
  renameWorkspace,
} from "@/api/plan";
import { useAdminMemberships } from "@/components/manage/org-context";
import { CreatePlanDialog, type CreateKind } from "@/components/plan/CreatePlanDialog";
import { DeskScreen } from "@/components/plan/DeskScreen";
import { ProgressBar } from "@/components/plan/ProgressBar";
import { StatusPill } from "@/components/plan/StatusPill";
import { useDocumentTitle } from "@/lib/document-title";

export const Route = createFileRoute("/_app/plan/all-work")({
  component: AllWork,
});

function fmtUpdated(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "recently";
  const diff = Date.now() - then;
  const day = 86_400_000;
  if (diff < day) return "today";
  if (diff < 2 * day) return "yesterday";
  if (diff < 7 * day) return `${Math.round(diff / day)}d ago`;
  if (diff < 30 * day) return `${Math.round(diff / (7 * day))}w ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

const cap = (s?: string | null) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

// Goal-type label for the objective badge — mirrors the mockup's GOAL_TYPE map
// ("Committed goal" / "Stretch goal") rather than a bare capitalized enum value.
const goalLabel = (type?: string | null): string =>
  type === "aspirational" ? "Stretch goal" : type === "committed" ? "Committed goal" : cap(type);

// Owner avatar initial — owners arrive as opaque ids (no display name on the
// list reads), so we surface the first alphanumeric char, falling back to the
// mockup's neutral "·" placeholder when there's no owner.
const ownerInitial = (id?: string | null) => {
  const m = (id ?? "").match(/[a-z0-9]/i);
  return m ? m[0].toUpperCase() : "·";
};

type Tab = "" | "workspace" | "project" | "objective";

// Card overflow ⋮ menu — Rename / Duplicate / Archive (mockup .pc-overflow +
// .card-menu / CardMenu). `onDuplicate` is optional (objectives can't duplicate).
function CardMenu({
  onRename,
  onDuplicate,
  onArchive,
}: {
  onRename: () => void;
  onDuplicate?: () => void;
  onArchive?: () => void;
}) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const stop = (fn: () => void) => (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    fn();
  };
  return (
    <span className="pc-menu-wrap" ref={ref}>
      <button
        type="button"
        className="pc-overflow"
        title="More"
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          setOpen((o) => !o);
        }}
      >
        <MoreVertical size={16} />
      </button>
      {open && (
        <div className="card-menu" onClick={(e) => e.preventDefault()}>
          <button type="button" className="cm-item" onClick={stop(onRename)}>
            Rename
          </button>
          {onDuplicate && (
            <button type="button" className="cm-item" onClick={stop(onDuplicate)}>
              Duplicate
            </button>
          )}
          {onArchive && (
            <>
              <div className="cm-sep" />
              <button type="button" className="cm-item danger" onClick={stop(onArchive)}>
                Archive
              </button>
            </>
          )}
        </div>
      )}
    </span>
  );
}

function AllWork() {
  useDocumentTitle(["All work", "Plan"]);
  const api = useApi();
  const queryClient = useQueryClient();
  const [createKind, setCreateKind] = useState<CreateKind>(null);
  const [q, setQ] = useState("");
  const [tab, setTab] = useState<Tab>(""); // "" = All work
  // Create W/P/O is org-admin/owner only (backend B5 is the hard gate; this
  // hides the affordance). useAdminMemberships() is org_owner/org_admin (C4).
  const isOrgAdmin = useAdminMemberships().length > 0;

  // All workspaces in the org (reuses the sidebar's warm ["workspaces"] cache).
  const workspaces = useQuery({ queryKey: ["workspaces"], queryFn: () => api.listWorkspaces() });
  const allWorkspaces = workspaces.data ?? [];
  // Plan mode is workspace-independent; new projects/objectives are created in
  // the org's first workspace by default (the Create dialog's target).
  const workspaceId = allWorkspaces[0]?.id;
  const loading = workspaces.isLoading;

  // Org-wide: fan out projects + objectives across every workspace, reusing the
  // per-workspace ["plan", …, wsId] keys the sidebar/detail screens warm.
  const projectQueries = useQueries({
    queries: allWorkspaces.map((w) => ({
      queryKey: ["plan", "projects", w.id],
      queryFn: () => listProjects(w.id),
    })),
  });
  const objectiveQueries = useQueries({
    queries: allWorkspaces.map((w) => ({
      queryKey: ["plan", "objectives", w.id],
      queryFn: () => listObjectives(w.id),
    })),
  });
  const projectsLoading = workspaces.isLoading || projectQueries.some((x) => x.isLoading);
  const objectivesLoading = workspaces.isLoading || objectiveQueries.some((x) => x.isLoading);
  const allProjects = projectQueries.flatMap((x) => x.data ?? []);
  const allObjectives = objectiveQueries.flatMap((x) => x.data ?? []);

  // Invalidate ALL workspaces' lists (prefix match) after a card mutation.
  const invalidateProjects = () =>
    queryClient.invalidateQueries({ queryKey: ["plan", "projects"] });
  const invalidateObjectives = () =>
    queryClient.invalidateQueries({ queryKey: ["plan", "objectives"] });

  // Project card actions → real plan API (rename / duplicate / archive).
  const renameProject = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => patchProject(id, { name }),
    onSuccess: () => void invalidateProjects(),
    onError: (e) => toast.error((e as Error).message),
  });
  const dupProject = useMutation({
    mutationFn: (id: string) => duplicateProject(id),
    onSuccess: () => {
      void invalidateProjects();
      toast.success("Project duplicated");
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const archiveProject = useMutation({
    mutationFn: (id: string) => patchProject(id, { archived: true }),
    onSuccess: () => {
      void invalidateProjects();
      toast.success("Project archived");
    },
    onError: (e) => toast.error((e as Error).message),
  });

  // Objective card actions → real OKR API (rename / archive; no duplicate endpoint).
  const renameObjective = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => patchObjective(id, { name }),
    onSuccess: () => void invalidateObjectives(),
    onError: (e) => toast.error((e as Error).message),
  });
  const archiveObjective = useMutation({
    mutationFn: (id: string) => patchObjective(id, { archived: true }),
    onSuccess: () => {
      void invalidateObjectives();
      toast.success("Objective archived");
    },
    onError: (e) => toast.error((e as Error).message),
  });

  // Workspace card → rename only (mojo-owned; proxied via admin console, org-admin
  // gated). No delete surfaced here (per product call: All-work is not the place
  // to destroy a workspace + its data).
  const renameWs = useMutation({
    mutationFn: ({ id, name }: { id: string; name: string }) => renameWorkspace(id, name),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["workspaces"] });
      toast.success("Workspace renamed");
    },
    onError: (e) => toast.error((e as Error).message),
  });

  const promptRename = (current: string): string | null => {
    const next = window.prompt("Rename", current);
    const trimmed = next?.trim();
    return trimmed && trimmed !== current ? trimmed : null;
  };

  const busy = loading || projectsLoading || objectivesLoading;
  const isEmpty =
    !busy &&
    allProjects.length === 0 &&
    allObjectives.length === 0 &&
    allWorkspaces.length === 0;

  const ql = q.trim().toLowerCase();
  const match = (name: string, desc?: string | null) =>
    !ql || name.toLowerCase().includes(ql) || (desc ?? "").toLowerCase().includes(ql);
  const prF = allProjects.filter((p) => match(p.name, p.description));
  const obF = allObjectives.filter((o) => match(o.name, o.description));
  const wsF = allWorkspaces.filter((w) => match(w.name, w.slug));

  const showWorkspaces = tab === "" || tab === "workspace";
  const showProjects = tab === "" || tab === "project";
  const showObjectives = tab === "" || tab === "objective";
  const prNeg = allProjects.filter((p) => p.status === "at_risk" || p.status === "blocked").length;
  const obNeg = allObjectives.filter((o) => o.status === "at_risk" || o.status === "off_track").length;

  const visWorkspaces = showWorkspaces ? wsF : [];
  const visProjects = showProjects ? prF : [];
  const visObjectives = showObjectives ? obF : [];
  const noResults =
    !busy &&
    !isEmpty &&
    visWorkspaces.length === 0 &&
    visProjects.length === 0 &&
    visObjectives.length === 0;

  return (
    <DeskScreen>
      {/* All-work overview: header + filters sit on the desk (.desk-toolbar);
          the white sheet (.canvas-surface) begins at the work grid — matching
          the mockup's ProjectShell desk/sheet separation (item 6). */}
      <div className="ov-allwork">
        <div className="desk-toolbar ov-toolbar">
          <div className="title-row">
            <div>
              <h1 className="page">All work</h1>
              <p className="page-sub">Projects and objectives across your organization.</p>
            </div>
            <div className="spacer" />
            {isOrgAdmin && (
              <DropdownMenu>
                <DropdownMenuTrigger render={<button type="button" className="btn primary" />}>
                  <Plus size={15} />
                  Create
                  <span className="create-caret">
                    <ChevronDown size={13} />
                  </span>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="min-w-[248px]">
                  <DropdownMenuGroup>
                    <DropdownMenuLabel className="font-mono text-[9px] uppercase tracking-[0.1em] text-muted-foreground">
                      Create
                    </DropdownMenuLabel>
                    <DropdownMenuItem render={<Link to="/workspaces" />} className="gap-2.5">
                      <Building2 className="size-4 text-muted-foreground" />
                      <span className="flex flex-col">
                        <span className="text-[13.5px] font-medium">Organizational Workspace</span>
                        <span className="text-[11.5px] text-muted-foreground">
                          A permanent area of responsibility
                        </span>
                      </span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setCreateKind("project")} className="gap-2.5">
                      <FolderKanban className="size-4 text-muted-foreground" />
                      <span className="flex flex-col">
                        <span className="text-[13.5px] font-medium">Project</span>
                        <span className="text-[11.5px] text-muted-foreground">
                          A time-bounded body of work
                        </span>
                      </span>
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setCreateKind("objective")} className="gap-2.5">
                      <Target className="size-4 text-muted-foreground" />
                      <span className="flex flex-col">
                        <span className="text-[13.5px] font-medium">Objective</span>
                        <span className="text-[11.5px] text-muted-foreground">
                          A strategic outcome (OKR)
                        </span>
                      </span>
                    </DropdownMenuItem>
                  </DropdownMenuGroup>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {!busy && !isEmpty && (
            <>
              <div className="ov-filterbar">
                <button
                  type="button"
                  className={`ovf-chip ${tab === "" ? "on" : ""}`}
                  onClick={() => setTab("")}
                >
                  All work
                  <span className="ovf-n">
                    {allWorkspaces.length + allProjects.length + allObjectives.length}
                  </span>
                </button>
                <button
                  type="button"
                  className={`ovf-chip ${tab === "workspace" ? "on" : ""}`}
                  onClick={() => setTab((t) => (t === "workspace" ? "" : "workspace"))}
                >
                  <Building2 size={13} /> Workspaces
                  <span className="ovf-n">{allWorkspaces.length}</span>
                </button>
                <button
                  type="button"
                  className={`ovf-chip ${tab === "project" ? "on" : ""}`}
                  onClick={() => setTab((t) => (t === "project" ? "" : "project"))}
                >
                  <FolderKanban size={13} /> Projects
                  <span className="ovf-n">{allProjects.length}</span>
                  {prNeg > 0 && (
                    <span className="ovf-neg" title="Needs attention">
                      {prNeg}
                    </span>
                  )}
                </button>
                <button
                  type="button"
                  className={`ovf-chip ${tab === "objective" ? "on" : ""}`}
                  onClick={() => setTab((t) => (t === "objective" ? "" : "objective"))}
                >
                  <Target size={13} /> Objectives
                  <span className="ovf-n">{allObjectives.length}</span>
                  {obNeg > 0 && (
                    <span className="ovf-neg" title="Needs attention">
                      {obNeg}
                    </span>
                  )}
                </button>
              </div>

              <div className="searchbar" style={{ margin: 0 }}>
                <Search size={16} className="search-ico" />
                <input
                  className="search-input"
                  placeholder={`Search ${
                    tab === "workspace"
                      ? "workspaces"
                      : tab === "project"
                        ? "projects"
                        : tab === "objective"
                          ? "objectives"
                          : "all work"
                  }…`}
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                />
              </div>
            </>
          )}
        </div>

        {/* data-tour anchor: the demo guided tour spotlights the all-work
            board (workspaces + projects + objectives on the white sheet). */}
        <div className="canvas-surface ov-sheet" data-tour="plan-board">
          {busy && (
            <div className="proj-grid">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-44 rounded-md" />
              ))}
            </div>
          )}

          {isEmpty && (
            <Empty>
              <EmptyTitle>No work yet</EmptyTitle>
              <EmptyDescription>
                Create a project to plan execution, or an objective to track outcomes.
              </EmptyDescription>
            </Empty>
          )}

          {showWorkspaces && wsF.length > 0 && (
            <div className="ov-group">
              {tab === "" && (
                <div className="ov-sec-head">
                  <h2 className="sec ov-sec">
                    Organization Workspaces <span className="ov-count">{wsF.length}</span>
                  </h2>
                </div>
              )}
              <div className="proj-grid">
                {wsF.map((w) => (
                  <Link
                    key={w.id}
                    to="/plan/w/$slug"
                    params={{ slug: w.slug }}
                    className="proj-card pc-ws"
                  >
                    <div className="pc-name">{w.name}</div>
                    <div className="pc-desc pc-desc-clamp">{w.slug}</div>
                    <div className="pc-ws-foot">
                      <span className="pc-avatar" title={w.name}>
                        {w.name.trim().charAt(0).toUpperCase() || "·"}
                      </span>
                      <span className="pc-foot-sep">·</span>
                      <span className="pc-updated">Updated {fmtUpdated(w.updated_at)}</span>
                      <CardMenu
                        onRename={() => {
                          const name = promptRename(w.name);
                          if (name) renameWs.mutate({ id: w.id, name });
                        }}
                      />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {showProjects && prF.length > 0 && (
            <div className="ov-group">
              {tab === "" && (
                <div className="ov-sec-head">
                  <h2 className="sec ov-sec">
                    Projects <span className="ov-count">{prF.length}</span>
                  </h2>
                </div>
              )}
              <div className="proj-grid">
                {prF.map((p) => (
                  <Link
                    key={p.id}
                    to="/plan/p/$projectSlug"
                    params={{ projectSlug: p.slug }}
                    className="proj-card pc-proj"
                  >
                    <StatusPill status={p.status} />
                    <div className="pc-name">{p.name}</div>
                    <div className="pc-desc pc-desc-clamp">{p.description}</div>
                    <div className="pc-prog-min">
                      <ProgressBar value={p.progress} variant="pc" />
                      <span className="pc-prog-pct">{p.progress}%</span>
                    </div>
                    <div className="pc-ws-foot">
                      <span className="pc-avatar" title={p.created_by ?? "Owner"}>
                        {ownerInitial(p.created_by)}
                      </span>
                      <span className="pc-foot-sep">·</span>
                      <span className="pc-updated">Updated {fmtUpdated(p.updated_at)}</span>
                      <CardMenu
                        onRename={() => {
                          const name = promptRename(p.name);
                          if (name) renameProject.mutate({ id: p.id, name });
                        }}
                        onDuplicate={() => dupProject.mutate(p.id)}
                        onArchive={() => archiveProject.mutate(p.id)}
                      />
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {showObjectives && obF.length > 0 && (
            <div className="ov-group">
              {tab === "" && (
                <div className="ov-sec-head">
                  <h2 className="sec ov-sec">
                    Objectives <span className="ov-count">{obF.length}</span>
                  </h2>
                </div>
              )}
              <div className="proj-grid">
                {obF.map((o) => {
                  const projectCount = o.contributing_project_count ?? 0;
                  return (
                    <Link
                      key={o.id}
                      to="/plan/o/$objectiveSlug"
                      params={{ objectiveSlug: o.slug }}
                      className="proj-card pc-obj"
                    >
                      {/* Toprow is status-ONLY (approved design); goal type moves to
                          the stacked footer so the card never shows two top tags. */}
                      <div className="pc-obj-toprow">
                        <StatusPill status={o.status} />
                      </div>
                      <div className="pc-name">{o.name}</div>
                      <div className="pc-desc pc-desc-clamp">
                        {o.description ?? (o.period ?? " ")}
                      </div>
                      <div className="pc-obj-prog">
                        <div className="pc-obj-prog-top">
                          <span className="pc-stat-n">{o.progress}%</span>
                        </div>
                        <ProgressBar value={o.progress} variant="pc" />
                      </div>
                      <div className="pc-obj-lines">
                        <div className="pc-obj-line">
                          <CheckSquare size={13} /> {o.on_track_count} / {o.kr_count} Key Results
                        </div>
                        <div className="pc-obj-line">
                          <TrendingUp size={13} /> {projectCount} Project
                          {projectCount === 1 ? "" : "s"}
                        </div>
                      </div>
                      <div className="pc-ws-foot pc-obj-foot">
                        <span className="pc-avatar" title={o.owner_user_id ?? o.created_by ?? "Owner"}>
                          {ownerInitial(o.owner_user_id ?? o.created_by)}
                        </span>
                        <span className="pc-foot-stack">
                          {(o.goal_type || o.period) && (
                            <span className="pc-foot-a">
                              {o.goal_type && (
                                <span className={`pc-goal ${o.goal_type}`}>
                                  {goalLabel(o.goal_type)}
                                </span>
                              )}
                              {o.goal_type && o.period ? " · " : ""}
                              {o.period ?? ""}
                            </span>
                          )}
                          <span className="pc-foot-b">Updated {fmtUpdated(o.updated_at)}</span>
                        </span>
                        <CardMenu
                          onRename={() => {
                            const name = promptRename(o.name);
                            if (name) renameObjective.mutate({ id: o.id, name });
                          }}
                          onArchive={() => archiveObjective.mutate(o.id)}
                        />
                      </div>
                    </Link>
                  );
                })}
              </div>
            </div>
          )}

          {noResults && (
            <div className="loading" style={{ paddingTop: 24 }}>
              {ql ? `Nothing matches “${q.trim()}”.` : "Nothing here yet."}
            </div>
          )}
        </div>
      </div>

      {workspaceId && (
        <CreatePlanDialog
          workspaceId={workspaceId}
          kind={createKind}
          onOpenChange={(open) => !open && setCreateKind(null)}
        />
      )}
    </DeskScreen>
  );
}
